// Financials Command Centre — presentation components. Every number comes
// from GET /api/financials/dashboard (backend/financials.py) which is the one
// authoritative commercial calculation layer.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
} from "recharts";
import {
  BarChart3, TrendingUp, Percent, FileClock, Wallet, AlertTriangle, Zap, Coins, Clock,
  Target, Search, Download, ArrowRight, ArrowUp, ArrowDown, Check, Sparkles, Receipt,
  ChevronRight, Info,
} from "lucide-react";
import { KiriCharacter } from "@/components/kiri/KiriCharacter";

export const CARD = "bg-[#121821] border border-[#1E2733] rounded-lg";
export const ACCENT = "#FF5F15";

export const money = (n, dp = 0) => {
  const v = Number(n || 0);
  const s = Math.abs(v).toLocaleString("en-NZ", { minimumFractionDigits: dp, maximumFractionDigits: dp });
  return `${v < 0 ? "-" : ""}$${s}`;
};
const money2 = (n) => money(n, 2);
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;

export const askKiri = (question) => {
  window.dispatchEvent(new CustomEvent("fl:kiri-ask", { detail: { question } }));
};

// ---------- shared shells ----------

export const SectionCard = ({ icon: Icon, title, meta, right, children, className = "", testid }) => (
  <section className={`${CARD} ${className}`} data-testid={testid}>
    <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#1E2733] flex-wrap">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: "rgba(255,95,21,0.14)" }}>
          <Icon className="w-4 h-4" style={{ color: ACCENT }} strokeWidth={2.4} />
        </span>
        <h2 className="text-[12px] font-black uppercase tracking-[0.12em] text-white truncate">{title}</h2>
        {meta && <span className="text-[11px] text-zinc-500 tabular-nums">{meta}</span>}
      </div>
      {right}
    </header>
    {children}
  </section>
);

const Delta = ({ pctChange, pp, suffix = "vs last period", invert = false, unit = "%" }) => {
  const raw = pctChange != null ? pctChange : pp;
  if (raw == null) return <div className="text-[11px] text-zinc-500 mt-1">No comparison data</div>;
  const up = raw >= 0;
  const good = invert ? !up : up;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <div className={`text-[11px] font-bold mt-1 flex items-center gap-1 ${good ? "text-emerald-400" : "text-red-400"}`}>
      <Icon className="w-3 h-3" strokeWidth={3} />
      {Math.abs(raw).toFixed(1)}{unit}
      <span className="text-zinc-500 font-medium ml-1">{suffix}</span>
    </div>
  );
};

// ---------- KPI row ----------

const KPI_TONES = {
  orange: ["rgba(255,95,21,0.14)", ACCENT],
  green: ["rgba(16,185,129,0.14)", "#34D399"],
  blue: ["rgba(59,130,246,0.14)", "#60A5FA"],
  amber: ["rgba(245,158,11,0.14)", "#FBBF24"],
  red: ["rgba(239,68,68,0.14)", "#F87171"],
  grey: ["rgba(113,113,122,0.16)", "#A1A1AA"],
};

const KpiCard = ({ icon: Icon, tone, label, value, valueClass = "text-white", sub, delta, hint, onClick, testid }) => {
  const [bg, fg] = KPI_TONES[tone] || KPI_TONES.grey;
  return (
    <button type="button" onClick={onClick} disabled={!onClick} title={hint}
      className={`${CARD} text-left px-3.5 py-3 transition-colors ${onClick ? "hover:border-[#31404F] cursor-pointer" : "cursor-default"} focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF5F15]`}
      data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: bg }}>
          <Icon className="w-3.5 h-3.5" style={{ color: fg }} strokeWidth={2.6} />
        </span>
        <span className="text-[10px] font-black uppercase tracking-[0.14em] text-zinc-400 truncate">{label}</span>
        {hint && <Info className="w-3 h-3 text-zinc-600 shrink-0" />}
      </div>
      <div className={`text-[26px] leading-none font-black tabular-nums mt-2.5 ${valueClass}`}>{value}</div>
      {delta}
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </button>
  );
};

export const KpiRow = ({ k, nav }) => {
  const rev = k?.revenue || {}; const gp = k?.gross_profit || {}; const mg = k?.margin || {};
  const wip = k?.unbilled_wip || {}; const out = k?.outstanding || {}; const od = k?.overdue || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="financials-kpis">
      <KpiCard icon={BarChart3} tone="orange" label="Revenue" value={money(rev.value)}
        delta={<Delta pctChange={rev.change_pct} />} hint="Invoiced revenue for the selected period."
        onClick={() => nav("/app/customer-invoices")} testid="kpi-revenue" />
      <KpiCard icon={TrendingUp} tone="green" label="Gross profit" value={money(gp.value)}
        valueClass={Number(gp.value) < 0 ? "text-red-400" : "text-white"}
        delta={<Delta pctChange={gp.change_pct} />} hint="Revenue less tracked job costs."
        testid="kpi-gross-profit" />
      <KpiCard icon={Percent} tone={Number(mg.value) < 0 ? "red" : "grey"} label="Margin" value={pct(mg.value)}
        valueClass={Number(mg.value) < 0 ? "text-red-400" : "text-white"}
        delta={<Delta pp={mg.change_pp} unit="pp" />} hint="Gross profit as a percentage of revenue."
        testid="kpi-margin" />
      <KpiCard icon={FileClock} tone="blue" label="Unbilled WIP" value={money(wip.value)}
        sub={`${wip.job_count || 0} job${wip.job_count === 1 ? "" : "s"} ready to invoice`}
        hint="Approved billable work that has not yet been invoiced."
        onClick={() => nav("/app/reports/job-billing")} testid="kpi-wip" />
      <KpiCard icon={Wallet} tone="amber" label="Outstanding" value={money(out.value)}
        sub={`${out.count || 0} invoice${out.count === 1 ? "" : "s"}`}
        hint="Remaining balance on unpaid customer invoices."
        onClick={() => nav("/app/customer-invoices")} testid="kpi-outstanding" />
      <KpiCard icon={AlertTriangle} tone="red" label="Overdue" value={money(od.value)}
        valueClass={Number(od.value) > 0 ? "text-red-400" : "text-white"}
        sub={`${od.count || 0} invoice${od.count === 1 ? "" : "s"}`}
        hint="Unpaid invoices past their due date."
        onClick={() => nav("/app/customer-invoices?status=overdue")} testid="kpi-overdue" />
    </div>
  );
};

// ---------- trend chart ----------

const monthLabel = (mk) => {
  if (!mk) return "";
  const [y, m] = mk.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-NZ", { month: "short" });
};

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload || {};
  const [y, m] = (label || "").split("-");
  const title = y ? new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-NZ", { month: "long", year: "numeric" }) : label;
  const rows = [["Revenue", money2(p.revenue), "text-emerald-400"], ["Cost", money2(p.cost), "text-zinc-300"],
    ["Gross Profit", money2(p.gross_profit), "text-[#FF9A5C]"], ["Margin", pct(p.margin), "text-white"]];
  return (
    <div className="bg-[#0B0F14] border border-[#2A3644] rounded-md px-3 py-2.5 shadow-xl">
      <div className="text-[10px] font-black uppercase tracking-[0.14em] text-zinc-400">{title}</div>
      <dl className="mt-1.5 space-y-1">
        {rows.map(([kk, v, cls]) => (
          <div key={kk} className="flex items-center justify-between gap-6 text-[11px]">
            <dt className="text-zinc-400">{kk}</dt>
            <dd className={`font-bold tabular-nums ${cls}`}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export const TrendChart = ({ trend, months, onMonthsChange }) => {
  const hasData = (trend || []).some((m) => m.revenue || m.cost);
  return (
    <SectionCard icon={BarChart3} title="Revenue, cost & profit" testid="financials-trend"
      right={
        <div className="flex items-center gap-3 flex-wrap">
          <select value={months} onChange={(e) => onMonthsChange(Number(e.target.value))}
            className="bg-[#0E141C] border border-[#25313E] text-zinc-300 text-[11px] font-bold uppercase tracking-wider rounded-sm px-2 py-1.5 outline-none focus:border-[#FF5F15]"
            data-testid="trend-months-select">
            {[3, 6, 12].map((n) => <option key={n} value={n}>Last {n} months</option>)}
          </select>
          <div className="hidden sm:flex items-center gap-3 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
            {[["Revenue", "#10B981"], ["Cost", "#8A94A6"], ["Gross Profit", ACCENT], ["Margin %", ACCENT]].map(([l, c]) => (
              <span key={l} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: c }} /> {l}
              </span>
            ))}
          </div>
        </div>
      }>
      <div className="px-2 pt-4 pb-2 h-[290px]">
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={trend} margin={{ top: 4, right: 8, left: 4, bottom: 0 }}>
              <CartesianGrid stroke="#1B2430" vertical={false} />
              <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fill: "#8A94A6", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="l" tickFormatter={(v) => money(v)} tick={{ fill: "#8A94A6", fontSize: 10 }} axisLine={false} tickLine={false} width={62} />
              <YAxis yAxisId="r" orientation="right" domain={[0, "auto"]} tickFormatter={(v) => `${Math.round(v)}%`} tick={{ fill: "#8A94A6", fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
              <ChartTooltip content={<TrendTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar yAxisId="l" dataKey="revenue" name="Revenue" fill="#10B981" radius={[2, 2, 0, 0]} barSize={13} />
              <Bar yAxisId="l" dataKey="cost" name="Cost" fill="#8A94A6" radius={[2, 2, 0, 0]} barSize={13} />
              <Bar yAxisId="l" dataKey="gross_profit" name="Gross Profit" fill={ACCENT} radius={[2, 2, 0, 0]} barSize={13} />
              <Line yAxisId="r" type="linear" dataKey="margin" name="Margin %" stroke={ACCENT} strokeWidth={2}
                dot={{ r: 3, fill: "#0B0F14", stroke: ACCENT, strokeWidth: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center px-6" data-testid="trend-empty">
            <BarChart3 className="w-7 h-7 text-zinc-600" />
            <p className="text-sm text-zinc-400 mt-3 font-bold">No revenue or cost recorded yet</p>
            <p className="text-xs text-zinc-500 mt-1 max-w-sm">Once invoices, timesheets and materials land against jobs, your monthly revenue, cost and margin trend appears here.</p>
          </div>
        )}
      </div>
    </SectionCard>
  );
};

// ---------- commercial actions ----------

const ACTION_TONES = {
  red: ["#EF4444", "rgba(239,68,68,0.09)", "text-red-400"],
  amber: ["#F59E0B", "rgba(245,158,11,0.09)", "text-amber-400"],
  blue: ["#3B82F6", "rgba(59,130,246,0.09)", "text-blue-400"],
};

export const CommercialActions = ({ actions, nav }) => (
  <SectionCard icon={Zap} title="Commercial actions" testid="financials-actions" className="h-full">
    <div className="p-3 space-y-2.5">
      {(actions || []).length === 0 ? (
        <div className="flex items-start gap-3 px-3 py-6" data-testid="actions-all-clear">
          <span className="w-8 h-8 rounded-md bg-emerald-500/15 flex items-center justify-center shrink-0">
            <Check className="w-4 h-4 text-emerald-400" strokeWidth={3} />
          </span>
          <div>
            <div className="text-sm font-black uppercase tracking-wide text-white">All clear</div>
            <p className="text-xs text-zinc-500 mt-1">Nothing currently needs commercial attention.</p>
          </div>
        </div>
      ) : (
        (actions || []).map((a) => {
          const [bar, bg, txt] = ACTION_TONES[a.tone] || ACTION_TONES.blue;
          return (
            <div key={a.id} className="flex items-center justify-between gap-3 rounded-md pl-3 pr-2.5 py-3 flex-wrap"
              style={{ background: bg, borderLeft: `3px solid ${bar}` }} data-testid={`action-${a.id}`}>
              <div className="min-w-0">
                <div className={`text-sm font-black ${txt} tabular-nums`}>{a.title}</div>
                <div className="text-[11px] text-zinc-400 mt-0.5">{a.detail}</div>
              </div>
              <button type="button" onClick={() => nav(a.action_url)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#0E141C] border border-[#25313E] text-[10px] font-black uppercase tracking-[0.12em] text-zinc-200 hover:border-[#FF5F15] hover:text-white transition-colors shrink-0"
                data-testid={`action-cta-${a.id}`}>
                {a.cta} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })
      )}
    </div>
  </SectionCard>
);

// ---------- cash position + ageing ----------

export const CashPosition = ({ cash }) => {
  const cells = [
    { label: "Invoiced this period", value: money(cash?.invoiced), delta: <Delta pctChange={cash?.invoiced_change_pct} suffix="" /> },
    { label: "Paid this period", value: money(cash?.paid), delta: <Delta pctChange={cash?.paid_change_pct} suffix="" /> },
    { label: "Outstanding", value: money(cash?.outstanding), sub: `${cash?.outstanding_count || 0} invoice${cash?.outstanding_count === 1 ? "" : "s"}` },
  ];
  if (cash?.avg_days_to_pay != null) {
    cells.push({
      label: "Average days to pay",
      value: cash.avg_days_to_pay === 0 ? "Same day" : `${cash.avg_days_to_pay} days`,
      delta: cash.avg_days_change != null
        ? <Delta pctChange={cash.avg_days_change} unit="" suffix="days vs last period" invert />
        : null,
    });
  }
  return (
    <SectionCard icon={Coins} title="Cash position" testid="financials-cash" className="h-full">
      <div className={`grid grid-cols-2 ${cells.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} divide-x divide-[#1E2733]`}>
        {cells.map((c) => (
          <div key={c.label} className="px-4 py-4">
            <div className="text-[9px] font-black uppercase tracking-[0.14em] text-zinc-500">{c.label}</div>
            <div className="text-[22px] leading-none font-black text-white tabular-nums mt-2">{c.value}</div>
            {c.delta}
            {c.sub && <div className="text-[11px] text-zinc-500 mt-1">{c.sub}</div>}
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

const AGEING_ROWS = [
  ["current", "Current", "#10B981"],
  ["d1_30", "1 – 30 days", "#FBBF24"],
  ["d31_60", "31 – 60 days", "#F97316"],
  ["d60_plus", "60+ days", "#EF4444"],
];

export const AgeingCard = ({ ageing, nav }) => {
  const total = Number(ageing?.total || 0);
  return (
    <SectionCard icon={Clock} title="Accounts receivable ageing" testid="financials-ageing" className="h-full"
      right={
        <button onClick={() => nav("/app/customer-invoices")}
          className="text-[10px] font-black uppercase tracking-[0.12em] text-zinc-400 hover:text-white flex items-center gap-1"
          data-testid="ageing-view-all">
          View all <ArrowRight className="w-3 h-3" />
        </button>
      }>
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#1E2733]">
        {AGEING_ROWS.map(([key, label, colour]) => {
          const v = Number(ageing?.[key] || 0);
          const w = total > 0 ? Math.max((v / total) * 100, v > 0 ? 6 : 0) : 0;
          return (
            <div key={key} className="px-4 py-4" data-testid={`ageing-${key}`}>
              <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500">{label}</div>
              <div className="text-[20px] leading-none font-black text-white tabular-nums mt-2">{money(v)}</div>
              <div className="mt-2.5 h-1.5 bg-[#1B2430] rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${w}%`, background: colour }} />
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

// ---------- job profitability ----------

const marginTone = (m, target) => {
  if (m == null) return ["#3F4A57", "text-zinc-500"];
  if (m < 0) return ["#EF4444", "text-red-400"];
  if (m < target * 0.6) return ["#EF4444", "text-red-400"];
  if (m < target) return ["#F59E0B", "text-amber-400"];
  return ["#10B981", "text-emerald-400"];
};

const MarginBar = ({ margin, target }) => {
  const [colour, txt] = marginTone(margin, target);
  const w = margin == null ? 0 : Math.max(Math.min(margin, 100), 0);
  return (
    <div className="flex items-center gap-2.5 justify-end">
      <div className="w-full max-w-[130px] h-2 bg-[#1B2430] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${w}%`, background: colour }} />
      </div>
      <span className={`text-xs font-black tabular-nums w-[54px] text-right ${txt}`}>
        {margin == null ? "—" : pct(margin)}
      </span>
      {margin != null && margin < target && (
        <AlertTriangle className={`w-3.5 h-3.5 ${margin < 0 ? "text-red-400" : "text-amber-400"}`} title="Below target margin" />
      )}
    </div>
  );
};

export const JobProfitability = ({ rows, total, target, search, onSearch, status, onStatus, onExport, nav }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? rows : rows.slice(0, 20);
  return (
    <SectionCard icon={Target} title="Job profitability" meta={`(${rows.length}/${total})`} testid="financials-job-profitability"
      right={
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search jobs, number or client…"
              className="bg-[#0E141C] border border-[#25313E] text-zinc-200 placeholder:text-zinc-600 text-xs rounded-sm pl-8 pr-3 py-2 w-[210px] outline-none focus:border-[#FF5F15]"
              data-testid="job-search" />
          </div>
          <select value={status} onChange={(e) => onStatus(e.target.value)}
            className="bg-[#0E141C] border border-[#25313E] text-zinc-300 text-[11px] font-bold uppercase tracking-wider rounded-sm px-2 py-2 outline-none focus:border-[#FF5F15]"
            data-testid="job-status-filter">
            <option value="all">All statuses</option>
            <option value="planned">Planned</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
            <option value="on_hold">On hold</option>
          </select>
          <button onClick={onExport}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#0E141C] border border-[#25313E] text-[10px] font-black uppercase tracking-[0.12em] text-zinc-200 hover:border-[#FF5F15] transition-colors"
            data-testid="job-export">
            <Download className="w-3.5 h-3.5" /> Export
          </button>
        </div>
      }>
      {/* desktop table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full border-collapse" data-testid="job-profitability-table">
          <thead>
            <tr className="text-[9px] font-black uppercase tracking-[0.14em] text-zinc-500 border-b border-[#1E2733]">
              <th scope="col" className="text-left px-4 py-2.5">Job</th>
              <th scope="col" className="text-left px-3 py-2.5">Client</th>
              <th scope="col" className="text-right px-3 py-2.5">Contract value</th>
              <th scope="col" className="text-right px-3 py-2.5">Revenue</th>
              <th scope="col" className="text-right px-3 py-2.5">Total cost</th>
              <th scope="col" className="text-right px-3 py-2.5">Gross profit</th>
              <th scope="col" className="text-right px-4 py-2.5 w-[260px]">Margin</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id} onClick={() => nav(`/app/jobs/${r.id}`)}
                className="border-b border-[#151E28] hover:bg-white/[0.03] cursor-pointer transition-colors"
                data-testid={`job-row-${r.job_number || r.id}`}>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <span className="px-1.5 py-0.5 rounded bg-[#1B2430] text-[10px] font-black text-zinc-400 tabular-nums shrink-0">{r.job_number || "—"}</span>
                    <span className="text-[13px] font-bold text-white truncate max-w-[320px]">{r.title || "Untitled job"}</span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-xs text-zinc-400 truncate max-w-[160px]">{r.client_name || "—"}</td>
                <td className="px-3 py-2.5 text-right text-xs text-zinc-300 tabular-nums">{r.contract_value == null ? "—" : money(r.contract_value)}</td>
                <td className="px-3 py-2.5 text-right text-xs text-zinc-200 tabular-nums">{money(r.total_revenue)}</td>
                <td className="px-3 py-2.5 text-right text-xs text-zinc-400 tabular-nums">{money(r.total_cost)}</td>
                <td className={`px-3 py-2.5 text-right text-xs font-black tabular-nums ${Number(r.gross_profit) < 0 ? "text-red-400" : "text-emerald-400"}`}>{money(r.gross_profit)}</td>
                <td className="px-4 py-2.5"><MarginBar margin={r.margin} target={target} /></td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-zinc-500" data-testid="job-profitability-empty">No jobs match this search.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* mobile / tablet cards */}
      <div className="lg:hidden divide-y divide-[#151E28]" data-testid="job-profitability-cards">
        {visible.map((r) => (
          <button key={r.id} onClick={() => nav(`/app/jobs/${r.id}`)} className="w-full text-left px-4 py-3.5 hover:bg-white/[0.03]">
            <div className="flex items-center gap-2">
              <span className="px-1.5 py-0.5 rounded bg-[#1B2430] text-[10px] font-black text-zinc-400 tabular-nums">{r.job_number || "—"}</span>
              <span className="text-[13px] font-black uppercase tracking-wide text-white truncate">{r.title || "Untitled job"}</span>
            </div>
            {r.client_name && <div className="text-[11px] text-zinc-500 mt-0.5">{r.client_name}</div>}
            <dl className="grid grid-cols-3 gap-2 mt-2.5">
              {[["Revenue", money(r.total_revenue), "text-zinc-200"], ["Cost", money(r.total_cost), "text-zinc-400"],
                ["Gross profit", money(r.gross_profit), Number(r.gross_profit) < 0 ? "text-red-400" : "text-emerald-400"]].map(([l, v, cls]) => (
                <div key={l}>
                  <dt className="text-[9px] font-black uppercase tracking-[0.12em] text-zinc-500">{l}</dt>
                  <dd className={`text-sm font-black tabular-nums ${cls}`}>{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-2.5"><MarginBar margin={r.margin} target={target} /></div>
            <div className="mt-2 flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.12em]" style={{ color: ACCENT }}>
              View job <ChevronRight className="w-3 h-3" />
            </div>
          </button>
        ))}
        {visible.length === 0 && <div className="px-4 py-10 text-center text-sm text-zinc-500">No jobs match this search.</div>}
      </div>

      {rows.length > 20 && (
        <div className="px-4 py-3 border-t border-[#1E2733]">
          <button onClick={() => setShowAll((v) => !v)}
            className="px-3 py-2 rounded-sm bg-[#0E141C] border border-[#25313E] text-[10px] font-black uppercase tracking-[0.12em] text-zinc-200 hover:border-[#FF5F15]"
            data-testid="job-show-all">
            {showAll ? "Show top 20 jobs" : `View all ${rows.length} jobs`}
          </button>
        </div>
      )}
    </SectionCard>
  );
};

// ---------- Kiri brief ----------

export const KiriBrief = ({ brief }) => (
  <SectionCard icon={Sparkles} title="Kiri's commercial brief" testid="financials-kiri"
    className="h-full"
    right={<span className="px-1.5 py-0.5 rounded bg-[#1B2430] text-[9px] font-black uppercase tracking-[0.14em] text-zinc-400">Beta</span>}>
    <div className="p-3 sm:p-4 flex gap-3 min-w-0 flex-wrap sm:flex-nowrap">
      <div className="shrink-0">
        <span className="hidden sm:block"><KiriCharacter state="idle" size="lg" /></span>
        <span className="sm:hidden block"><KiriCharacter state="idle" size="md" /></span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="rounded-lg rounded-tl-none bg-[#0E141C] border border-[#25313E] px-3.5 py-3">
          {(brief?.lines || []).length ? (
            <p className="text-[13px] leading-relaxed text-zinc-300" data-testid="kiri-brief-text">
              {brief.lines.join(" ")}
            </p>
          ) : (
            <p className="text-[13px] text-zinc-500">Not enough financial data yet — once jobs, invoices and costs are flowing I'll brief you here.</p>
          )}
        </div>
        <div className="mt-3">
          <div className="text-[9px] font-black uppercase tracking-[0.16em] text-zinc-500">Try asking</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {(brief?.questions || []).map((q) => (
              <button key={q} onClick={() => askKiri(q)}
                className="px-2.5 py-1.5 rounded-full bg-[#0E141C] border border-[#25313E] text-[11px] text-left text-zinc-300 hover:border-[#FF5F15] hover:text-white transition-colors max-w-full"
                data-testid={`kiri-chip-${q.slice(0, 12).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {q}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  </SectionCard>
);

// ---------- recent invoices / variations ----------

const INV_BADGE = {
  paid: ["Paid", "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"],
  due: ["Due", "bg-amber-500/15 text-amber-400 border-amber-500/30"],
  partially_paid: ["Part paid", "bg-amber-500/15 text-amber-300 border-amber-500/30"],
  overdue: ["Overdue", "bg-red-500/15 text-red-400 border-red-500/30"],
  void: ["Void", "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"],
};

const shortDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export const RecentInvoices = ({ invoices, nav }) => (
  <SectionCard icon={Receipt} title="Recent invoices" testid="financials-recent-invoices" className="h-full"
    right={
      <button onClick={() => nav("/app/customer-invoices")} className="text-[10px] font-black uppercase tracking-[0.12em] text-zinc-400 hover:text-white flex items-center gap-1" data-testid="invoices-view-all">
        View all <ArrowRight className="w-3 h-3" />
      </button>
    }>
    <div className="divide-y divide-[#151E28]">
      {(invoices || []).length === 0 && <div className="px-4 py-8 text-center text-sm text-zinc-500">No invoices yet.</div>}
      {(invoices || []).map((i) => {
        const [label, cls] = INV_BADGE[i.state] || INV_BADGE.due;
        return (
          <button key={i.id} onClick={() => nav(`/app/customer-invoices/${i.id}`)}
            className="w-full text-left px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3 min-w-0 hover:bg-white/[0.03] transition-colors"
            data-testid={`recent-invoice-${i.invoice_number}`}>
            <span className="text-[10px] font-black text-zinc-500 tabular-nums w-[46px] shrink-0">{i.invoice_number}</span>
            <span className="text-xs text-zinc-300 truncate flex-1 min-w-0">{i.client_name || "—"}</span>
            <span className="text-right shrink-0">
              <span className="block text-xs font-black text-white tabular-nums">{money2(i.total)}</span>
              {i.due_date && <span className="block text-[10px] text-zinc-500">Due {shortDate(i.due_date)}</span>}
            </span>
            <span className={`px-1.5 py-0.5 rounded border text-[9px] font-black uppercase tracking-wider shrink-0 ${cls}`}>{label}</span>
          </button>
        );
      })}
    </div>
  </SectionCard>
);

const VAR_BADGE = {
  approved: ["Approved", "text-emerald-400"],
  requested: ["Requested", "text-amber-400"],
  invoiced: ["Invoiced", "text-blue-400"],
  declined: ["Declined", "text-red-400"],
  draft: ["Draft", "text-zinc-400"],
};

export const RecentVariations = ({ variations, nav }) => (
  <SectionCard icon={TrendingUp} title="Recent variations" testid="financials-recent-variations" className="h-full"
    right={
      <button onClick={() => nav("/app/variations")} className="text-[10px] font-black uppercase tracking-[0.12em] text-zinc-400 hover:text-white flex items-center gap-1" data-testid="variations-view-all">
        View all <ArrowRight className="w-3 h-3" />
      </button>
    }>
    <div className="divide-y divide-[#151E28]">
      {(variations || []).length === 0 && <div className="px-4 py-8 text-center text-sm text-zinc-500">No variations yet.</div>}
      {(variations || []).map((v) => {
        const [label, cls] = VAR_BADGE[v.status] || VAR_BADGE.draft;
        return (
          <button key={v.id} onClick={() => nav(v.job_id ? `/app/jobs/${v.job_id}` : "/app/variations")}
            className="w-full text-left px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3 min-w-0 hover:bg-white/[0.03] transition-colors"
            data-testid={`recent-variation-${v.variation_number}`}>
            <span className="text-[10px] font-black text-zinc-500 tabular-nums w-[40px] shrink-0">{v.variation_number || "—"}</span>
            <span className="text-xs text-zinc-300 truncate flex-1 min-w-0">{v.job_title || "—"}</span>
            <span className="text-right shrink-0">
              <span className="block text-xs font-black text-white tabular-nums">{v.price == null ? "—" : money2(v.price)}</span>
              <span className={`block text-[10px] font-bold ${cls}`}>{label}</span>
            </span>
            <span className="hidden sm:block text-[9px] font-bold uppercase tracking-wider text-zinc-500 shrink-0 w-[64px] text-right">
              {v.invoiced ? "Invoiced" : "Not invoiced"}
            </span>
          </button>
        );
      })}
    </div>
  </SectionCard>
);

export const Skeleton = () => (
  <div className="space-y-3 animate-pulse" data-testid="financials-skeleton">
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
      {Array.from({ length: 6 }).map((_, i) => <div key={i} className={`${CARD} h-[104px]`} />)}
    </div>
    <div className="grid xl:grid-cols-12 gap-3">
      <div className={`${CARD} h-[350px] xl:col-span-8`} />
      <div className={`${CARD} h-[350px] xl:col-span-4`} />
    </div>
    <div className="grid xl:grid-cols-12 gap-3">
      <div className={`${CARD} h-[150px] xl:col-span-7`} />
      <div className={`${CARD} h-[150px] xl:col-span-5`} />
    </div>
    <div className={`${CARD} h-[420px]`} />
  </div>
);

export const useNav = () => {
  const navigate = useNavigate();
  return (url) => { if (url?.startsWith("#")) { document.querySelector(url)?.scrollIntoView({ behavior: "smooth" }); } else if (url) navigate(url); };
};
